import React, { useState, useRef } from 'react';
import { Upload, Link as LinkIcon, X, Loader2, ImageIcon } from 'lucide-react';
// Directo de cada archivo y no del barrel: el barrel arrastra el menu lateral y con el
// react-router, que las pruebas de este componente no necesitan.
import { Button } from './button';
import { Input } from './input';
import { Label } from './label';
import { Alert, AlertDescription } from './alert';
import { cn } from '../../lib/utils';
import { supabase } from '../../lib/supabase';

/**
 * ImageUploader - Componente para subir imágenes o usar URL externa
 *
 * @param {string} value - URL actual de la imagen
 * @param {function} onChange - Callback cuando cambia la URL (url) => void
 * @param {string} bucket - Nombre del bucket de Supabase (default: 'logos')
 * @param {string} folder - Carpeta dentro del bucket (default: '')
 * @param {string} label - Label del campo (default: 'Logo/Imagen')
 */
//
// El <input type="file"> nativo se pintaba tal cual dentro de un flex sin min-w-0: su
// ancho es el del NOMBRE del archivo, asi que con un nombre largo llegaba a ~1000px y
// desbordaba el modal entero hacia los lados (medido: 985px de input en un dialogo de
// 492px). El dueno lo vio en el alta de una liga desde el celular ("se descuadra todo",
// 2026-09-05). Ahora el input nativo va oculto y lo dispara un boton; el nombre se
// muestra aparte, recortado. Y sin colores fijos: tokens del tema.

// Lo que se ensena de una URL: el nombre del archivo, no la URL entera.
export const nombreDeArchivo = (url) => {
  if (!url) return '';
  try {
    const ultimo = decodeURIComponent(String(url).split('?')[0].split('/').pop() || '');
    // Los subidos llevan prefijo de fecha: 1788633060324_logo.png -> logo.png
    return ultimo.replace(/^\d{10,}_/, '');
  } catch {
    return String(url);
  }
};

const ImageUploader = ({
  value = '',
  onChange,
  bucket = 'logos',
  folder = '',
  label = 'Logo/Imagen'
}) => {
  const [mode, setMode] = useState(value && !value.includes('supabase') ? 'url' : 'upload');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [urlInput, setUrlInput] = useState(value || '');
  const [nombreElegido, setNombreElegido] = useState('');
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setNombreElegido(file.name);

    // Validar tipo MIME estrictamente (solo JPG/JPEG y PNG)
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type.toLowerCase())) {
      setError('Solo se permiten archivos JPG y PNG');
      return;
    }

    // Validar tamaño (máx 3MB)
    const maxSize = 3 * 1024 * 1024; // 3MB en bytes
    if (file.size > maxSize) {
      setError('El archivo no puede superar los 3MB');
      return;
    }

    // Validar extensión del nombre de archivo (seguridad adicional)
    const fileName = file.name.toLowerCase();
    const validExtensions = ['.jpg', '.jpeg', '.png'];
    const hasValidExtension = validExtensions.some(ext => fileName.endsWith(ext));

    if (!hasValidExtension) {
      setError('Extensión de archivo no permitida. Solo JPG y PNG');
      return;
    }

    try {
      setUploading(true);
      setError(null);

      // Generar nombre único
      const timestamp = Date.now();
      const fileName = `${timestamp}_${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
      const filePath = folder ? `${folder}/${fileName}` : fileName;

      // Subir archivo a Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from(bucket)
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      // Obtener URL pública
      const { data: { publicUrl } } = supabase.storage
        .from(bucket)
        .getPublicUrl(filePath);

      onChange(publicUrl);
      setUrlInput(publicUrl);
    } catch (err) {
      console.error('Error uploading image:', err);
      setError(err.message || 'Error al subir la imagen');
    } finally {
      setUploading(false);
    }
  };

  const handleUrlSubmit = () => {
    if (!urlInput.trim()) {
      setError('Ingresa una URL válida');
      return;
    }

    // Validar que sea una URL
    try {
      new URL(urlInput);
      onChange(urlInput);
      setError(null);
    } catch {
      setError('URL inválida');
    }
  };

  const handleClear = () => {
    onChange('');
    setUrlInput('');
    setNombreElegido('');
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // En la fila del boton solo el estado: el nombre lo dice la vista previa (una vez).
  const estadoArchivo = value ? '' : (nombreElegido || 'Ningún archivo elegido');

  return (
    <div className="min-w-0 max-w-full space-y-2">
      <div className="flex items-center justify-between gap-2">
        <Label>{label}</Label>
        <div className="flex shrink-0 gap-1">
          <button
            type="button"
            onClick={() => setMode('upload')}
            className={cn('rounded px-2 py-1 text-xs transition-colors', mode === 'upload' ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted')}
          >
            <Upload className="mr-1 inline h-3 w-3" />
            Subir
          </button>
          <button
            type="button"
            onClick={() => setMode('url')}
            className={cn('rounded px-2 py-1 text-xs transition-colors', mode === 'url' ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted')}
          >
            <LinkIcon className="mr-1 inline h-3 w-3" />
            URL
          </button>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription className="text-xs">{error}</AlertDescription>
        </Alert>
      )}

      {mode === 'upload' ? (
        <div className="space-y-1">
          {/* El input nativo va oculto: su ancho es el del nombre del archivo y
              desbordaba el modal. Lo dispara el boton; el nombre se muestra aparte. */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png"
            onChange={handleFileSelect}
            disabled={uploading}
            className="sr-only"
            aria-label={label}
          />
          <div className="flex min-w-0 items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="shrink-0"
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
            >
              {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
              {uploading ? 'Subiendo...' : 'Elegir imagen'}
            </Button>
            <span data-testid="estado-archivo" className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
              {estadoArchivo}
            </span>
            {value && !uploading && (
              <Button type="button" variant="ghost" size="sm" className="h-8 w-8 shrink-0 p-0" onClick={handleClear} aria-label="Quitar imagen">
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
          <p className="text-xs text-muted-foreground">Solo JPG y PNG (máx. 3MB)</p>
        </div>
      ) : (
        <div className="space-y-1">
          <div className="flex min-w-0 items-center gap-2">
            <Input
              type="url"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              onBlur={handleUrlSubmit}
              placeholder="https://ejemplo.com/imagen.jpg"
              className="min-w-0 flex-1"
            />
            {value && (
              <Button type="button" variant="ghost" size="sm" className="h-8 w-8 shrink-0 p-0" onClick={handleClear} aria-label="Quitar imagen">
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
          <p className="text-xs text-muted-foreground">Ingresa la URL de una imagen externa</p>
        </div>
      )}

      {/* Vista previa: la imagen y su nombre, no la URL entera. */}
      {value && (
        <div className="flex min-w-0 items-center gap-3 overflow-hidden rounded-lg border bg-muted/40 p-2">
          <img
            src={value}
            alt="Vista previa"
            className="h-14 w-14 shrink-0 rounded border bg-background object-contain"
            onError={(e) => {
              e.target.style.display = 'none';
              setError('Error al cargar la imagen');
            }}
          />
          <div className="flex min-w-0 flex-1 items-center gap-1.5 text-xs text-muted-foreground">
            <ImageIcon className="h-3.5 w-3.5 shrink-0" />
            <span data-testid="nombre-archivo" className="min-w-0 truncate">{nombreDeArchivo(value) || 'Imagen cargada'}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageUploader;
